import { cn } from "@/lib/utils";

/**
 * Building blocks for the therapist dossier.
 *
 * The page is read to make a decision, so every element here is tuned for
 * scanning: hairline separators instead of gaps, tabular numerals so figures
 * line up down a column, and a single small-caps heading per panel.
 */

export function Panel({
  title,
  count,
  action,
  children,
  className,
  bodyClassName,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("rounded-xl border bg-card shadow-sm", className)}>
      <header className="flex items-center justify-between gap-3 border-b border-border/70 px-4 py-2.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
          {title}
          {count !== undefined && (
            <span className="ml-1.5 tabular-nums text-muted-foreground/60">{count}</span>
          )}
        </h2>
        {action}
      </header>
      <div className={cn("p-4", bodyClassName)}>{children}</div>
    </section>
  );
}

/**
 * One row of a definition list. Labels sit in a fixed column so values form a
 * straight edge the eye can run down.
 */
export function Field({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] items-baseline gap-3 py-1.5">
      <dt className="text-xs leading-5 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "min-w-0 text-sm leading-5 text-foreground",
          mono && "tabular-nums"
        )}
      >
        {children}
      </dd>
    </div>
  );
}

/** Value shown when a field is empty, so blanks read as data rather than bugs. */
export function Empty({ children = "nije uneseno" }: { children?: React.ReactNode }) {
  return <span className="text-muted-foreground/50">{children}</span>;
}

export function FieldList({ children }: { children: React.ReactNode }) {
  return <dl className="divide-y divide-border/50">{children}</dl>;
}

/** Small neutral chip for tags, languages and specialisations. */
export function Chip({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "brand";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
        tone === "brand"
          ? "bg-primary/10 text-primary"
          : "bg-muted text-muted-foreground"
      )}
    >
      {children}
    </span>
  );
}
